import { useMemo } from "react";

import type {
	CustomerQuoteBoardItem,
	QuoteBoardItem,
} from "~/shared/schemas/quote-board-schema.ts";
import type { QuoteBoardChip } from "~/shared/types/quote-board-types.ts";
import { dateMatchesSearch } from "~/shared/utils/date-search.ts";

// Define searchable fields in one place for maintainability
const SEARCHABLE_FIELDS = [
	"quoteId",
	"source",
	"customerId",
	"customerName",
	"shipmentDate",
	"createdByName",
	"poNumber",
	"originAddress",
	"destinationAddress",
	"totalWeight",
	"customerRate",
	"statusLevelId",
	"statusLevel",
	"carrierName",
	"carrierRate",
	"maxLiability",
	"margin",
	"ltlOwnerId",
	"ltlOwner",
	"ftlOwnerId",
	"ftlOwner",
	"customerReferences",
	"createdDate",
	"expirationDate",
	"bulkQuoteId",
	"requestCount",
	"processedCount",
	"errorCount",
] as const;

type SearchableField = (typeof SEARCHABLE_FIELDS)[number];

const getFieldValue = (
	item: QuoteBoardItem,
	field: SearchableField,
): string => {
	const value = item[field];
	if (value == null) return "";
	if (typeof value === "string") return value.toLowerCase();
	if (typeof value === "number") return value.toString();
	return String(value).toLowerCase();
};

const DATE_FIELDS = new Set<SearchableField>([
	"shipmentDate",
	"createdDate",
	"expirationDate",
]);

const itemMatchesChip = (item: QuoteBoardItem, searchTerm: string): boolean => {
	return SEARCHABLE_FIELDS.some((field) => {
		const fieldValue = getFieldValue(item, field);
		if (DATE_FIELDS.has(field)) {
			return dateMatchesSearch(
				item[field] as string | null | undefined,
				searchTerm,
				"local",
			);
		}
		return fieldValue.includes(searchTerm);
	});
};

export const useQuoteBoardFilter = (
	gridData: (QuoteBoardItem | CustomerQuoteBoardItem)[] | undefined,
	chips: QuoteBoardChip[],
) => {
	return useMemo(() => {
		const safeData = (gridData ?? []).filter(
			(item): item is QuoteBoardItem =>
				item != null && typeof item === "object",
		);

		if (chips.length === 0) return safeData;

		return safeData.filter((item) =>
			chips.some((chip) => itemMatchesChip(item, chip.value.toLowerCase())),
		);
	}, [gridData, chips]);
};
